import React, { useEffect, useRef, useState } from "react";
import { Button, BusyIndicator, CheckBox, Input, TextArea, Title } from "@ui5/webcomponents-react";
import { toast } from "sonner";
import { AdminApi } from "../../api/client";

const apiError = (e, fallback) => {
  const data = e?.response?.data;
  if (data?.fields) return Object.values(data.fields)[0];
  return data?.message || fallback;
};

const deDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("de-DE") : null);
const bookableWindow = (l) => {
  const f = deDate(l.bookableFrom);
  const u = deDate(l.bookableUntil);
  if (f && u) return `${f} – ${u}`;
  if (f) return `ab ${f}`;
  if (u) return `bis ${u}`;
  return "unbegrenzt";
};

const PRESET_DURATIONS = [30, 45, 60, 90, 120];
const durationsLabel = (l) =>
  l.allowedDurations && l.allowedDurations.length ? `${l.allowedDurations.join(", ")} Min` : "alle";

function Field({ label, children }) {
  return (
    <div className="field-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

export default function LinksPage() {
  const [links, setLinks] = useState(null);
  const [label, setLabel] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [bookableFrom, setBookableFrom] = useState("");
  const [bookableUntil, setBookableUntil] = useState("");
  const [durations, setDurations] = useState([]); // selectable length options (minutes)
  const [selectedDurations, setSelectedDurations] = useState(() => new Set());
  const [createsMeet, setCreatesMeet] = useState(false); // new links default to in-person (no Meet)
  const [singleUse, setSingleUse] = useState(false);
  const [reminderLead, setReminderLead] = useState(1440); // minutes before appointment; 0 = off
  const [note, setNote] = useState("");
  const [busyId, setBusyId] = useState(null); // id of the row currently mutating

  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    setLinks(null);
    AdminApi.listLinks()
      .then((r) => { if (id === reqId.current) setLinks(r); })
      .catch(() => { if (id === reqId.current) setLinks([]); });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    AdminApi.slotDurations()
      .then((d) => setDurations([...new Set([...PRESET_DURATIONS, ...d])].sort((a, b) => a - b)))
      .catch(() => setDurations(PRESET_DURATIONS));
  }, []);

  const toggleDuration = (d) =>
    setSelectedDurations((prev) => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n; });

  const create = async () => {
    if (!label.trim()) { toast.error("Bitte ein Label angeben."); return; }
    try {
      await AdminApi.createLink({
        label: label.trim(),
        validUntil: validUntil || null,
        bookableFrom: bookableFrom || null,
        bookableUntil: bookableUntil || null,
        allowedDurations: [...selectedDurations],
        createsMeetLink: createsMeet,
        singleUse,
        reminderLeadMinutes: reminderLead,
        note: note.trim() || null,
      });
      toast.success("Buchungslink erstellt.");
      setLabel("");
      setValidUntil("");
      setBookableFrom("");
      setBookableUntil("");
      setSelectedDurations(new Set());
      setCreatesMeet(false);
      setSingleUse(false);
      setReminderLead(1440);
      setNote("");
      load();
    } catch (e) { toast.error(apiError(e, "Link konnte nicht erstellt werden.")); }
  };

  const toggle = async (l) => {
    if (busyId) return; // a mutation is already in flight
    setBusyId(l.id);
    try { await AdminApi.updateLink(l.id, { active: !l.active }); load(); }
    catch (e) { toast.error(apiError(e, "Aktualisierung fehlgeschlagen.")); }
    finally { setBusyId(null); }
  };

  const remove = async (l) => {
    if (busyId) return;
    setBusyId(l.id);
    try { await AdminApi.deleteLink(l.id); toast.success("Link gelöscht."); load(); }
    catch (e) { toast.error(apiError(e, "Löschen fehlgeschlagen.")); }
    finally { setBusyId(null); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link kopiert."); }
    catch { toast.message(url); }
  };

  return (
    <div>
      <Title level="H2" style={{ margin: "8px 0 16px" }}>Buchungslinks</Title>

      <div className="card">
        <h3>Neuen Link erstellen</h3>
        <div className="form-grid">
          <Field label="Label (z.B. Kampagne/Kontakt)">
            <Input value={label} style={{ width: "100%" }} onInput={(e) => setLabel(e.target.value)} />
          </Field>
          <Field label="Link gültig bis (optional)">
            <input className="native-input" type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </Field>
          <Field label="Buchbar von (optional)">
            <input className="native-input" type="date" value={bookableFrom} onChange={(e) => setBookableFrom(e.target.value)} />
          </Field>
          <Field label="Buchbar bis (optional)">
            <input className="native-input" type="date" value={bookableUntil} onChange={(e) => setBookableUntil(e.target.value)} />
          </Field>
          <Button design="Emphasized" icon="add" onClick={create}>Link erstellen</Button>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#4a5867", fontWeight: 600 }}>Erlaubte Längen:</span>
          {durations.map((d) => (
            <CheckBox key={d} text={`${d} Min`} checked={selectedDurations.has(d)} onChange={() => toggleDuration(d)} />
          ))}
          <span className="muted" style={{ fontSize: 12 }}>(keine = alle Längen)</span>
        </div>
        <div style={{ marginTop: 14 }}>
          <CheckBox text="Google-Meet-Link erzeugen (Online-Termin) – Standard: aus (Termin vor Ort)"
            checked={createsMeet} onChange={(e) => setCreatesMeet(e.target.checked)} />
        </div>
        <div style={{ marginTop: 4 }}>
          <CheckBox text="Nur einmal buchbar (Einmal-Link – nach Buchung gesperrt, bei Storno wieder frei)"
            checked={singleUse} onChange={(e) => setSingleUse(e.target.checked)} />
        </div>
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: "#4a5867", fontWeight: 600 }}>Erinnerung an Kunden:</label>
          <select className="native-input" style={{ width: "auto" }} value={reminderLead}
            onChange={(e) => setReminderLead(Number(e.target.value))}>
            <option value={0}>aus</option>
            <option value={60}>1 Std vorher</option>
            <option value={120}>2 Std vorher</option>
            <option value={180}>3 Std vorher</option>
            <option value={1440}>24 Std vorher</option>
            <option value={2880}>48 Std vorher</option>
          </select>
          <span className="muted" style={{ fontSize: 12 }}>(automatische Erinnerungs-Mail)</span>
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 12, color: "#4a5867", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Hinweis (optional – erscheint in Bestätigung &amp; Kalender)
          </label>
          <TextArea value={note} rows={2} style={{ width: "100%" }}
            placeholder="z.B. Platz 2, Musterstraße 1 · bitte 10 Min früher da sein"
            onInput={(e) => setNote(e.target.value)} />
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          „Erlaubte Längen“ zeigt nur Termine dieser Dauer (z.B. nur 30- oder 60-Minuten-Termine).
          „Buchbar von/bis“ begrenzt den <strong>Terminzeitraum</strong> (z.B. 01.–31.01.). „Link gültig bis“ steuert, wann der Link selbst abläuft.
        </p>
      </div>

      {links === null ? (
        <div className="center-screen"><BusyIndicator active /></div>
      ) : links.length === 0 ? (
        <p className="muted">Noch keine Links erstellt.</p>
      ) : (
        <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr><th>Label</th><th>Längen</th><th>Meet</th><th>Einmal</th><th>Aufrufe</th><th>Buchungen</th><th>Buchbar</th><th>Gültig bis</th><th>Status</th><th>Link</th><th></th></tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.id}>
                <td>{l.label}{l.note ? <span title={l.note} style={{ marginLeft: 6, cursor: "help" }}>📝</span> : null}</td>
                <td>{durationsLabel(l)}</td>
                <td>{l.createsMeetLink ? "Ja" : "Nein"}</td>
                <td>{l.singleUse ? "Ja" : "Nein"}</td>
                <td>{l.visitCount ?? 0}</td>
                <td>{(l.bookingCount ?? 0)}{(l.visitCount > 0) ? ` (${Math.min(100, Math.round((l.bookingCount ?? 0) / l.visitCount * 100))}%)` : ""}</td>
                <td>{bookableWindow(l)}</td>
                <td>{l.validUntil ? new Date(l.validUntil).toLocaleString("de-DE") : "unbegrenzt"}</td>
                <td>
                  {l.expired ? <span className="badge cancelled">abgelaufen</span>
                    : l.active ? <span className="badge available">aktiv</span>
                    : <span className="badge pending">inaktiv</span>}
                </td>
                <td>
                  <div className="copy-row">
                    <code style={{ maxWidth: 280 }}>{l.url}</code>
                    <Button design="Transparent" icon="copy" onClick={() => copy(l.url)} />
                  </div>
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <Button design="Transparent" icon={l.active ? "hide" : "show"} disabled={busyId === l.id} onClick={() => toggle(l)} />
                  <Button design="Transparent" icon="delete" disabled={busyId === l.id} onClick={() => remove(l)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
